import { useAuth } from 'contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { AdminPermissionClaims } from '@otw/models/user';

/**
 * Visibility scope for lead management features (leads, customers, tasks).
 *
 * - `none`: user cannot see anything in lead management.
 * - `own`: user can only see records where they are the assigned sales rep.
 * - `all`: user can see every record regardless of assignment.
 */
export type LeadManagementScope = 'none' | 'own' | 'all'

/**
 * Resolve the lead management visibility scope from a user's permissions array,
 * with backwards compatibility for the legacy single `view` flag.
 */
const resolveLeadManagementScope = (
  permissions: AdminPermissionClaims[] | null | undefined,
): LeadManagementScope => {
  if (!permissions || permissions.length === 0) return 'none'

  let scope: LeadManagementScope = 'none'
  for (const permissionObj of permissions) {
    const lm = permissionObj?.lead_management as
      | (AdminPermissionClaims['lead_management'] & { view?: boolean })
      | undefined
    if (!lm) continue

    if (lm.view_all === true) return 'all'
    if (lm.view_own === true) {
      scope = 'own'
      continue
    }
    // Legacy data: a single `view: true` flag without the new fields.
    // Treat it as the safer "own" scope; admins can opt users up to "all".
    if (lm.view === true && lm.view_own === undefined && lm.view_all === undefined) {
      scope = 'own'
    }
  }
  return scope
}

export const usePermissions = () => {
  const { userInfo } = useAuth()

  const { data: permissions, isLoading: isPermissionsLoading } =  useQuery<AdminPermissionClaims[] | null>({
    queryKey: ['permissions', userInfo?.id],
    queryFn: async () => {
      if (!userInfo) {
        return null
      }

      // Extract permissions directly from userInfo
      return userInfo.permissions || null
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!userInfo,
  })

  const checkPermission = (category: keyof AdminPermissionClaims, action: string) => {
    if (category === 'lead_operations') {
      if (!permissions || permissions.length === 0) {
        return userInfo?.isOwner ?? false
      }
      const hasExplicitGroup = permissions.some(
        permissionObj => permissionObj.lead_operations !== undefined,
      )
      if (!hasExplicitGroup) {
        return userInfo?.isOwner ?? false
      }
      return permissions.some(permissionObj => {
        const leadOps = permissionObj.lead_operations
        if (!leadOps) return false
        return leadOps[action as keyof typeof leadOps] === true
      })
    }

    if (!permissions || permissions.length === 0) return false;

    // Special-case: legacy `lead_management.view` action maps to the new
    // scope-based fields. Treat it as "user can see anything in lead mgmt".
    if (category === 'lead_management' && action === 'view') {
      return resolveLeadManagementScope(permissions) !== 'none'
    }

    // Check if any permission object in the array grants the requested permission
    return permissions.some(permissionObj => {
      const categoryPermissions = permissionObj[category];
      if (!categoryPermissions) return false;

      return categoryPermissions[action as keyof typeof categoryPermissions] === true;
    });
  }

  const leadManagementScope: LeadManagementScope = resolveLeadManagementScope(permissions)

  return { permissions, checkPermission, isPermissionsLoading, leadManagementScope }
}
